import React, { useState } from "react";
import {
  Box,
  Button,
  Input,
  Select,
  FormControl,
  FormLabel,
  Text,
  useToast,
  VStack,
  Link,
  HStack,
  Badge,
} from "@chakra-ui/react";
import { updatePreferences } from "../api/updatePreferences";
import PlanComparisonCarousel from "./PlanComparisonCarousel";
import { useAuth } from "../hooks/useAuth";
import PaystackButton from "./PaystackButton";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const budgetPlanDescriptions = {
  "Survival": "Best for tight budgets. Simple, healthy meals.",
  "Beta Plan": "Balanced meals for everyday living.",
  "Chop Life Gang": "Premium, gourmet meals for foodies.",
};

const avgCostMap = {
  "Survival": 1000,
  "Beta Plan": 1600,
  "Chop Life Gang": 3500,
};

function computeElitePrice(budgetPlan, mealsPerDay, daysInMonth = 30) {
  const avgCost = avgCostMap[budgetPlan] || 2000;
  const base = mealsPerDay * daysInMonth * avgCost;
  return Math.round(base * 1.3); // 30% markup
}

function EditProfileForm({
  preferences = {},
  setMongoUser,
  setMealPlan,
  refetchMealPlan,
  mongoUser,
}) {
  const toast = useToast();
  const [dietType, setDietType] = useState(preferences.dietType || "");
  const [allergies, setAllergies] = useState((preferences.allergies || []).join(", "));
  const [goals, setGoals] = useState((preferences.goals || []).join(", "));
  const [healthConditions, setHealthConditions] = useState((preferences.healthConditions || []).join(", "));
  const [accessTier, setAccessTier] = useState(preferences.accessTier || "");
  const [budgetPlan, setBudgetPlan] = useState(preferences.budgetPlan || "");
  const [loading, setLoading] = useState(false);
  const [mealsPerDay, setMealsPerDay] = useState(preferences.mealsPerDay || 3);
  const [wallet, setWallet] = useState(0);
  const { user } = useAuth();
  const daysInMonth = 30;
  const elitePrice = (accessTier === "Elite" && budgetPlan && mealsPerDay)
    ? computeElitePrice(budgetPlan, mealsPerDay, daysInMonth)
    : 0;

  const publicKey = "pk_live_cf1853432ca0b93e4b472038a979dd2ae0c3d82e";

  const onPaystackSuccess = async (ref) => {
    toast({
      title: "Payment successful!",
      description: "Your wallet has been funded.",
      status: "success",
      duration: 3000,
      isClosable: true,
    });
    setWallet((w) => w + elitePrice);
    // Save preferences and grant access
    await savePreferences(true);
  };

  const onPaystackClose = () => {
    toast({
      title: "Payment cancelled",
      description: "You can try again.",
      status: "info",
      duration: 3000,
      isClosable: true,
    });
  };

  const savePreferences = async (skipPaymentCheck = false) => {
    setLoading(true);
    try {
      // Validate comma-separated fields
      const allergyArr = allergies.split(",").map((a) => a.trim()).filter(Boolean);
      const goalsArr = goals.split(",").map((g) => g.trim()).filter(Boolean);
      const healthArr = healthConditions.split(",").map((h) => h.trim()).filter(Boolean);
      if (allergyArr.some((a) => a.length < 2) || goalsArr.some((g) => g.length < 2)) {
        toast({
          title: "Please enter valid allergies and goals.",
          status: "warning",
          isClosable: true,
        });
        setLoading(false);
        return;
      }
      if ((accessTier === "Elite" || accessTier === "Smart") && (!budgetPlan || !mealsPerDay)) {
        toast({
          title: "Please select a Budget Plan and Meals Per Day.",
          status: "warning",
          isClosable: true,
        });
        setLoading(false);
        return;
      }
      if (!skipPaymentCheck && accessTier === "Elite" && wallet < elitePrice) {
        toast({
          title: "Please complete payment to subscribe.",
          status: "warning",
          isClosable: true,
        });
        setLoading(false);
        return;
      }
      const updates = {
        dietType,
        allergies: allergyArr,
        goals: goalsArr,
        healthConditions: healthArr,
        accessTier,
        budgetPlan: (accessTier === "Flex" || accessTier === "Elite") ? budgetPlan : "",
        mealsPerDay,
      };
      const res = await updatePreferences(updates);
      if (setMongoUser) {
        setMongoUser((prev) => ({ ...prev, preferences: res.preferences }));
      }
      if (accessTier === "Flex" || skipPaymentCheck) {
        // ✅ Fetch from Cloud Run backend dynamically
        const token = await window.firebase.auth().currentUser.getIdToken();
        const response = await fetch(`${API_BASE_URL}/api/user/meal-plan`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) throw new Error("Meal plan fetch failed");
        const newMeals = await response.json();
        if (newMeals && Array.isArray(newMeals)) {
          if (res.preferences && refetchMealPlan) refetchMealPlan();
          localStorage.setItem("latestMealPlan", JSON.stringify(newMeals));
          setMongoUser((prev) => ({ ...prev, mealPlan: newMeals }));
          setMealPlan(newMeals);
        }
      }
      toast({
        title: "Preferences updated",
        description: accessTier === "Flex" ? "Meal plan refreshed" : "Subscription active!",
        status: "success",
        isClosable: true,
      });
    } catch (err) {
      console.error("Update failed:", err);
      toast({
        title: "Failed to update preferences",
        description: err.message,
        status: "error",
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (accessTier === "Elite") {
      if (!budgetPlan || !mealsPerDay) {
        toast({
          title: "Please select a Budget Plan and Meals Per Day.",
          status: "warning",
          isClosable: true,
        });
        return;
      }
      if (wallet < elitePrice) {
        toast({
          title: "Please complete payment to subscribe.",
          status: "warning",
          isClosable: true,
        });
        return;
      }
      savePreferences(true);
    } else {
      savePreferences();
    }
  };

  return (
    <Box as="form" onSubmit={handleSubscribe} mt={4}>
      <VStack spacing={4} align="stretch">
        <FormControl>
          <FormLabel>Diet Type</FormLabel>
          <Input
            value={dietType}
            onChange={(e) => setDietType(e.target.value)}
            placeholder="e.g. Vegetarian, Keto"
          />
        </FormControl>

        <FormControl>
          <FormLabel>Allergies (comma-separated)</FormLabel>
          <Input
            value={allergies}
            onChange={(e) => setAllergies(e.target.value)}
            placeholder="e.g. peanuts, dairy"
          />
          <Text fontSize="xs" color="gray.500">Separate multiple allergies with commas</Text>
        </FormControl>

        <FormControl>
          <FormLabel>Health Goals</FormLabel>
          <Input
            value={goals}
            onChange={(e) => setGoals(e.target.value)}
            placeholder="e.g. weight loss, diabetes management"
          />
          <Text fontSize="xs" color="gray.500">e.g. weight loss, diabetes management. Separate with commas.</Text>
        </FormControl>

        <FormControl>
          <FormLabel>Chronic Conditions</FormLabel>
          <Input
            value={healthConditions}
            onChange={(e) => setHealthConditions(e.target.value)}
            placeholder="e.g. hypertension, diabetes"
          />
          <Text fontSize="xs" color="gray.500">e.g. hypertension, diabetes. Separate with commas.</Text>
        </FormControl>

        <FormControl>
          <FormLabel>Access Tier</FormLabel>
          <Select
            value={accessTier}
            onChange={(e) => setAccessTier(e.target.value)}
            placeholder="Select your Serena Plan"
          >
            <option value="Flex">Flex (Pay as you go)</option>
            <option value="Smart">Smart (₦5,000/mo)</option>
            <option value="Elite">Elite (Full subscription)</option>
          </Select>
        </FormControl>

        {(accessTier === "Flex" || accessTier === "Elite") && (
          <FormControl>
            <FormLabel>Budget Plan</FormLabel>
            <Select
              value={budgetPlan}
              onChange={(e) => setBudgetPlan(e.target.value)}
              placeholder="Select your preferred plan"
            >
              <option value="Survival">Survival - {budgetPlanDescriptions["Survival"]}</option>
              <option value="Beta Plan">Beta Plan - {budgetPlanDescriptions["Beta Plan"]}</option>
              <option value="Chop Life Gang">Chop Life Gang - {budgetPlanDescriptions["Chop Life Gang"]}</option>
            </Select>
          </FormControl>
        )}

        <FormControl>
          <FormLabel>Meals Per Day</FormLabel>
          <Select
            value={mealsPerDay}
            onChange={(e) => setMealsPerDay(parseInt(e.target.value))}
          >
            {[1, 2, 3, 4, 5].map((val) => (
              <option key={val} value={val}>{val} meal{val > 1 ? "s" : ""} per day</option>
            ))}
          </Select>
        </FormControl>

        {accessTier === "Elite" && budgetPlan && mealsPerDay && (
          <HStack>
            <Badge colorScheme="green" fontSize="lg">Elite Plan Price:</Badge>
            <Text fontSize="xl" color="green.700" fontWeight="bold">₦{elitePrice.toLocaleString()} / month</Text>
          </HStack>
        )}

        {(accessTier === "Flex" || accessTier === "Elite") && (
          <Box mt={4}>
            <PlanComparisonCarousel
              isSubscribed={user?.subscriptionStatus === "subscribed"}
              selected={budgetPlan}
              onSelect={setBudgetPlan}
              mealsPerDay={mealsPerDay}
              budgetPlan={budgetPlan}
            />
          </Box>
        )}

        <Text fontSize="sm" mt={2}>
          Learn how we protect your data in our <Link to="/app/privacy" style={{ color: "#319795", fontWeight: 500 }}>Privacy Policy</Link>.
        </Text>
        <Text fontSize="xs" color="gray.400" mt={1} textAlign="center">
          &copy; {new Date().getFullYear()} Serena Space. All rights reserved.
        </Text>
        {accessTier === "Elite" && (
          <PaystackButton
            publicKey={publicKey}
            email={user?.email || "guest@example.com"}
            amount={elitePrice * 100}
            onSuccess={onPaystackSuccess}
            onClose={onPaystackClose}
            disabled={loading || wallet >= elitePrice}
            style={{ background: '#24C16C', color: 'white', border: 'none', padding: '8px 16px', borderRadius: 4, cursor: 'pointer', opacity: loading || wallet >= elitePrice ? 0.6 : 1, marginBottom: 8 }}
          >
            {wallet >= elitePrice ? 'Payment Complete' : `Pay Now (₦${elitePrice.toLocaleString()})`}
          </PaystackButton>
        )}
        <Button type="submit" isLoading={loading} colorScheme="teal" alignSelf="flex-start" mt={3}>
          Subscribe Now
        </Button>
        <HStack mt={2}>
          <Badge colorScheme="purple">Wallet Balance:</Badge>
          <Text fontWeight="bold">₦{wallet.toLocaleString()}</Text>
        </HStack>
      </VStack>
    </Box>
  );
}

export default EditProfileForm;
